import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";

/**
 * Listens for the TooLost OAuth redirect that lands on the configured redirect
 * URI (production: https://distributemusic.ai/dashboard, which mounts our app
 * with `?code=…&state=tl_…` query params).
 *
 * On detection:
 *  1) POST { code, state } to /api/admin/toolost/oauth/callback
 *  2) Toast success or failure
 *  3) Clean the URL (drop code/state) and redirect the admin to /admin/toolost.
 */
export default function ToolostCallbackHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const handled  = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(location.search);
    const code   = params.get("code");
    const state  = params.get("state");
    if (!code || !state || !state.startsWith("tl_")) return;
    handled.current = true;

    (async () => {
      try {
        await api.post("/admin/toolost/oauth/callback", { code, state });
        toast.success("Distribution partner connected successfully");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Distribution partner callback failed");
      } finally {
        // Strip code/state from the URL and route the admin to the management page.
        params.delete("code");
        params.delete("state");
        const cleaned = params.toString();
        navigate(`/admin/toolost${cleaned ? `?${cleaned}` : ""}`, { replace: true });
      }
    })();
  }, [location.search, navigate]);

  return null;
}
